import React from 'react';

interface Props {
  children: React.ReactNode;
  /** Optional friendly name shown in the fallback (e.g. "Admin Dashboard"). */
  scope?: string;
  /** Optional custom fallback renderer. Receives the error + a reset callback. */
  fallback?: (err: Error, reset: () => void) => React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * ErrorBoundary — catches render-time exceptions inside a route subtree so a
 * crash in (say) the admin dashboard does not blank out the entire customer
 * shell. Logs to the console and offers a "Try again" + "Reload" UI.
 *
 * React error boundaries must be class components — there is no hook
 * equivalent as of React 18.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.scope ? `:${this.props.scope}` : ''}]`, error, info);
    // Hook for Sentry / Datadog later:
    //   (window as any).Sentry?.captureException(error, { extra: info });
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        role="alert"
        style={{
          maxWidth: 560,
          margin: '64px auto',
          padding: '24px 28px',
          background: '#fff',
          border: '1px solid #fecaca',
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ fontSize: 32, lineHeight: 1, marginBottom: 8 }}>😬</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>
          Something went wrong{this.props.scope ? ` in ${this.props.scope}` : ''}.
        </h2>
        <p style={{ margin: '0 0 16px', color: '#475569', fontSize: 14 }}>
          {error.message || 'An unexpected error occurred while rendering this page.'}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={this.reset}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid #16a34a',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              background: '#fff',
              color: '#0f172a',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;

